/**
 * Loads the vendored mediasoup-client bundle (`/vendor/mediasoup-client.mjs`). This is
 * the one thing every session must do exactly once before it can build any WebRTC
 * transport at all — mediasoup-client is not bundled with the app, it's fetched at
 * runtime so the SFU/vendor pairing can be swapped without a rebuild.
 */
import type { MediasoupClientModule } from "./mediasoup-module";

export const MEDIASOUP_VENDOR_PATH = "/vendor/mediasoup-client.mjs";
const MEDIASOUP_IMPORT_MS = 120_000;

function getLocation(): Location | null {
  try {
    return typeof globalThis !== "undefined" && globalThis.location ? globalThis.location : null;
  } catch {
    return null;
  }
}

/** Build the vendor mediasoup-client URL (same semantics as the legacy transport code). */
export function resolveVendorMediasoupUrl(opts: { cacheBust?: boolean } = {}): string {
  // A dynamic import() that fails once (network drop, dev-server restart) gets its
  // specifier "poisoned" by the browser's module map — later import() calls for the
  // SAME url reject immediately with the same error, without ever hitting the network
  // again, even once connectivity is back. A fresh query param forces a new specifier
  // so a retry can actually retry. Only added after a real prior failure (see
  // `loadMediasoupClient` below) so the normal/first-load case still benefits from the
  // browser's module cache across reconnects.
  const loc = getLocation();
  if (loc?.href) {
    const url = new URL(MEDIASOUP_VENDOR_PATH, loc.href);
    url.searchParams.set("v", __APP_VERSION__);
    if (opts.cacheBust) url.searchParams.set("retry", String(Date.now()));
    return url.href;
  }
  const retry = opts.cacheBust ? `&retry=${Date.now()}` : "";
  return `${MEDIASOUP_VENDOR_PATH}?v=${__APP_VERSION__}${retry}`;
}

/**
 * Dynamic import with a hard timeout.
 *
 * `T` is supplied by the caller (this file asserts the vendor bundle's actual
 * module-namespace shape) — the function itself has no way to know what a runtime URL
 * resolves to, so it cannot verify `T` for you; that's exactly why this is a generic
 * `Promise<T>` and not a bare `unknown` pushed onto every caller.
 */
async function importWithTimeout<T>(url: string, ms: number, label: string): Promise<T> {
  // Definite-assignment (!): assigned synchronously inside the Promise executor below,
  // which always runs before `.finally()`'s callback could possibly read it.
  let tid!: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, rej) => {
    tid = setTimeout(() => rej(new Error(`${label}: нет ответа за ${ms / 1000}s (${url})`)), ms);
  });
  return Promise.race([
    (import(/* @vite-ignore */ url) as Promise<T>).catch((err) => {
      const message = err instanceof Error ? err.message : String(err);
      throw new Error(`${label}: ${message} (${url})`);
    }),
    timeout,
  ]).finally(() => clearTimeout(tid));
}

let importFailedBefore = false;

export async function loadMediasoupClient(
  sig: (msg: string) => void
): Promise<MediasoupClientModule> {
  const url = resolveVendorMediasoupUrl({ cacheBust: importFailedBefore });
  sig(`2/7 mediasoup-client: ${url}`);
  try {
    return await importWithTimeout<MediasoupClientModule>(
      url,
      MEDIASOUP_IMPORT_MS,
      "mediasoup-client (vendor)"
    );
  } catch (err) {
    // Without this, every retry re-requests the SAME url (once cache-busted, it stays
    // cache-busted for the rest of the page's lifetime) — the browser's module map
    // otherwise poisons that specifier after one failure and every later import() for
    // the identical url rejects instantly with the same error, network back or not.
    importFailedBefore = true;
    throw err;
  }
}
